import React from "react";
import "./MediaEntertainment.css";
import { useNavigate } from "react-router-dom";
import {
  FaFilm,
  FaBroadcastTower,
  FaRobot,
  FaCogs,
  FaShieldAlt,
  FaChartLine,
  FaCloud,
  FaMicrophone,
  FaClosedCaptioning,
  FaSyncAlt,
  FaGlobe,
  FaUsers,
} from "react-icons/fa";

const MediaEntertainment = () => {
  const navigate = useNavigate();

  return (
    <div className="me-container">

      {/* Hero Header */}
      <div className="me-header-section">
        <h1 className="me-header-title">Media & Entertainment</h1>
        <p className="me-header-subtitle">
          Powering the Future of Digital Media with Intelligent Solutions
        </p>
      </div>

      {/* Intro */}
      <div className="me-section me-intro">
        <p className="me-text">
          In today's fast-evolving media and entertainment landscape, organizations need more than just
          technology — they need a partner who understands the complexity of digital content operations,
          audience engagement, and marketing ecosystems. We deliver end-to-end solutions tailored specifically
          for media companies, broadcasters, streaming platforms, and entertainment brands looking to scale,
          automate, and innovate.
        </p>
      </div>

      {/* Who We Serve */}
      <div className="me-section me-who-serve">
        <h2 className="me-section-title">
          <FaUsers className="me-icon" /> Who We Serve
        </h2>
        <p className="me-text">We work with a wide range of clients across the media and entertainment spectrum, including:</p>
        <div className="me-cards-grid">
          {[
            { icon: <FaFilm />, label: "Digital media companies and content publishers" },
            { icon: <FaBroadcastTower />, label: "Broadcasting and streaming platforms" },
            { icon: <FaChartLine />, label: "Entertainment marketing agencies" },
            { icon: <FaCloud />, label: "OTT (Over-the-Top) content providers" },
            { icon: <FaCogs />, label: "Advertising and campaign management teams" },
          ].map((item, i) => (
            <div key={i} className="me-card">
              <div className="me-card-icon">{item.icon}</div>
              <p>{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stock image — Digital Media */}
      <div className="me-image-banner">
        <img
          src="https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1400&q=80&auto=format&fit=crop"
          alt="Digital media and broadcasting studio"
          className="me-banner-img"
        />
        <div className="me-banner-overlay">
          <h3>End-to-End Digital Media Solutions</h3>
        </div>
      </div>

      {/* What We Do */}
      <div className="me-section">
        <h2 className="me-section-title">
          <FaCogs className="me-icon" /> What We Do
        </h2>

        <div className="me-services-list">
          {[
            {
              title: "Digital Content Workflow Management",
              text: "We design and develop robust systems that streamline how your content moves from creation to delivery. Our solutions eliminate manual bottlenecks, automate repetitive processes, and ensure your digital content reaches the right audience at the right time — without disruption.",
            },
            {
              title: "Web Application Development",
              text: "We build scalable, dynamic web applications tailored to the demands of media operations. Whether it's a content management portal, a campaign dashboard, or a client-facing platform, our applications are built to handle high volumes of traffic and transactions while delivering a seamless user experience.",
            },
            {
              title: "Marketing Platform Integration",
              text: "We bridge the gap between your marketing tools and backend systems, enabling your teams to run campaigns more efficiently. Our integration services ensure that data flows smoothly across your platforms, giving your marketing operations a unified, real-time view of performance.",
            },
            {
              title: "Operational Dashboards & Reporting",
              text: "We develop intelligent dashboards and automated reporting systems that give your leadership and operations teams the visibility they need. From content performance metrics to campaign analytics, our reporting solutions turn raw data into actionable business insights — helping you make faster, smarter decisions.",
            },
            {
              title: "API & Systems Integration",
              text: "Modern media platforms rely on multiple systems working in harmony. We develop and maintain reliable API connections between your frontend applications, backend services, and third-party platforms — ensuring consistent, secure, and uninterrupted data exchange across your entire ecosystem.",
            },
            {
              title: "Quality Assurance & Testing",
              text: "We take software quality seriously. Our QA process includes thorough test planning, functional testing, integration testing, and regression testing — all aligned with your business requirements. We validate every release before it goes live, so your audience always gets a stable, polished experience.",
            },
            {
              title: "Secure Content & Access Management",
              text: "Protecting your content and user data is a top priority. We implement industry-standard security practices to ensure that access to your platforms and content is controlled, compliant, and protected against unauthorized use.",
            },
            {
              title: "Production Support & Deployment",
              text: "From staging to go-live, we manage smooth production rollouts with minimal downtime. Our team supports deployment transitions, monitors post-launch stability, and maintains detailed documentation to keep your systems running reliably long after launch.",
            },
          ].map((item, i) => (
            <div key={i} className="me-service-item">
              <h3 className="me-service-title">{item.title}</h3>
              <p className="me-text">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stock image — Streaming */}
      <div className="me-image-banner">
        <img
          src="https://images.unsplash.com/photo-1536240478700-b869ad10e128?w=1400&q=80&auto=format&fit=crop"
          alt="Streaming and entertainment technology"
          className="me-banner-img"
        />
        <div className="me-banner-overlay">
          <h3>AI-Powered Broadcasting & Media Innovation</h3>
        </div>
      </div>

      {/* AI Broadcasting Section */}
      <div className="me-section me-ai-section">
        <h2 className="me-section-title">
          <FaRobot className="me-icon" /> AI-Powered Broadcasting & Media Innovation
        </h2>
        <p className="me-text">
          As artificial intelligence reshapes the media and entertainment industry, we are at the forefront
          of building intelligent systems that transform how content is created, delivered, and experienced.
          Our AI capabilities go beyond automation — we help media organizations unlock new levels of
          efficiency, creativity, and audience engagement.
        </p>

        <div className="me-ai-subsection">
          <h3 className="me-subsection-title">
            <FaBroadcastTower className="me-icon-sm" /> AI News Anchor & Broadcast Automation
          </h3>
          <p className="me-text">
            We are developing next-generation AI-assisted broadcasting platforms that redefine how news and
            media content is produced and delivered. Our solution brings together intelligent script
            generation, automated subtitle creation, and AI-driven media rendering.
          </p>
          <div className="me-feature-cards">
            {[
              {
                icon: <FaCogs />,
                title: "Automated Script Orchestration",
                desc: "AI systems that generate, manage, and deliver broadcast-ready scripts with minimal human intervention, reducing production time significantly.",
              },
              {
                icon: <FaClosedCaptioning />,
                title: "Intelligent Subtitle Automation",
                desc: "End-to-end subtitle generation and synchronization workflows that ensure accessibility and compliance across all content formats.",
              },
              {
                icon: <FaMicrophone />,
                title: "AI Voice & Media Rendering",
                desc: "Integration of cloud-based AI voice synthesis with automated media pipelines, enabling realistic, scalable broadcast-ready content production.",
              },
              {
                icon: <FaSyncAlt />,
                title: "Content Orchestration at Scale",
                desc: "Centralized intelligent workflows that coordinate every stage of broadcast production — from content ingestion to final delivery.",
              },
            ].map((card, i) => (
              <div key={i} className="me-feature-card">
                <div className="me-feature-icon">{card.icon}</div>
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="me-ai-subsection">
          <h3 className="me-subsection-title">
            <FaMicrophone className="me-icon-sm" /> Smart AI Teleprompter & Live Studio Synchronization
          </h3>
          <p className="me-text">
            For live production environments, timing and precision are everything. We are building an
            intelligent teleprompter synchronization system designed for studios, live broadcasts, and
            production teams that demand real-time accuracy.
          </p>
          <div className="me-feature-cards">
            {[
              {
                icon: <FaSyncAlt />,
                title: "Real-Time Speech Synchronization",
                desc: "An AI-powered engine that listens, understands, and automatically keeps the teleprompter in sync with the presenter's natural speaking pace — no manual control needed.",
              },
              {
                icon: <FaCogs />,
                title: "AI-Assisted Script Management",
                desc: "Smart script rendering that adapts dynamically to live edits, pace changes, and multilingual content requirements during active production.",
              },
              {
                icon: <FaBroadcastTower />,
                title: "Low-Latency Live Communication",
                desc: "Ultra-fast, reliable communication infrastructure that ensures zero lag between the presenter and production systems in high-stakes live environments.",
              },
              {
                icon: <FaGlobe />,
                title: "Multilingual Support",
                desc: "Built to support diverse global production teams, with the ability to prompt in multiple languages seamlessly during live broadcasts.",
              },
            ].map((card, i) => (
              <div key={i} className="me-feature-card">
                <div className="me-feature-icon">{card.icon}</div>
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stock image — News studio */}
      <div className="me-image-banner">
        <img
          src="https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=1400&q=80&auto=format&fit=crop"
          alt="News broadcasting studio with AI technology"
          className="me-banner-img"
        />
        <div className="me-banner-overlay">
          <h3>Smart Teleprompter & Live Studio Synchronization</h3>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="me-section me-why-us">
        <h2 className="me-section-title">
          <FaShieldAlt className="me-icon" /> Why Choose Us for Media & Entertainment?
        </h2>
        <div className="me-why-grid">
          {[
            {
              title: "Industry-Aligned Expertise",
              desc: "We understand the unique pace and pressure of media operations and build solutions that keep up with it.",
            },
            {
              title: "AI-First Innovation",
              desc: "We don't just apply AI as an add-on; we architect AI-native systems built specifically for the demands of modern broadcasting and digital media.",
            },
            {
              title: "Scalable Architecture",
              desc: "Our solutions are designed to grow with your audience and content library, without compromising performance.",
            },
            {
              title: "End-to-End Ownership",
              desc: "From design and development to testing and deployment, we manage the full lifecycle of your digital products.",
            },
            {
              title: "Business-First Thinking",
              desc: "Every solution we build is tied to a clear business outcome — whether that's reducing operational costs, improving content delivery speed, or increasing audience engagement.",
            },
            {
              title: "Reliable Support",
              desc: "We don't just deliver and disappear. We provide ongoing support, optimization, and documentation to ensure long-term stability.",
            },
          ].map((item, i) => (
            <div key={i} className="me-why-card">
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="me-section me-cta text-center">
        <h2>Let's Build Something Great Together</h2>
        <p className="me-text">
          Whether you're modernizing legacy systems, launching a new digital platform, scaling your media
          operations, or embracing AI-powered broadcasting, we're ready to help.
        </p>
        <button
          className="me-cta-button"
          onClick={() => {
            navigate("/Contact-us");
            window.scrollTo(0, 0);
          }}
        >
          Get in Touch
        </button>
      </div>

    </div>
  );
};

export default MediaEntertainment;
